declare global {
    interface Window {
        grecaptcha: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

/**
 * Class responsible for handling Google Recaptcha v3 loading and form submission.
 */
export class RecaptchaFormHandler {
    private recaptchaLoaded: boolean;
    private readonly recaptchaPublicKey: string | null;

    constructor() {
        this.recaptchaLoaded = false;
        this.recaptchaPublicKey = this.getRecaptchaPublicKey();

        if (!this.recaptchaPublicKey) throw new Error("Recaptcha public key not found in meta tags.");

        this.loadRecaptcha();
        this.observeVisibility();
    }

    /**
     * Retrieves the Recaptcha public key from a meta tag.
     * @returns {string} The Recaptcha public key.
     */
    private getRecaptchaPublicKey(): string | null {
        const metaTag = document.querySelector('meta[name="recaptcha-public"]');
        return metaTag ? metaTag.getAttribute("content") : null;
    }

    private loadRecaptcha() {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaPublicKey}`;
        script.onload = () => {
            this.recaptchaLoaded = true;

            window.grecaptcha.ready(() => {
                this.attachSubmitHandlers();
                this.refreshTokens();
            });
        };

        document.head.appendChild(script);
    }

    /**
     * Attach the submit handlers and initialize the reCaptcha token.
     */
    private attachSubmitHandlers() {
        document.querySelectorAll("[data-recaptcha-form]").forEach((form) => {
            this.insertRecaptchaTokenField(form as HTMLFormElement);
            this.addSubmitHandler(form as HTMLFormElement);
        });
    }

    /**
     * Handle form submissions.
     *
     * @param form
     */
    private addSubmitHandler(form: HTMLFormElement) {
        let isSubmitting = false;

        form.addEventListener("submit", async (event) => {
            if (isSubmitting) return;

            const formId = form.id;
            const tokenField = form.querySelector(`input[name="_recaptcha_token_${formId}"]`) as HTMLInputElement | null;

            if (!tokenField) return; // or handle error

            const issuedAt = Number(tokenField.dataset.issuedAt || 0);
            const age = Math.floor(Date.now() / 1000) - issuedAt;

            // refresh if missing OR older than 90s (tweak threshold)
            if (!tokenField.value || age > 90) {
                event.preventDefault();
                isSubmitting = true;

                try {
                    await this.setTokenForForm(form);
                    form.requestSubmit ? form.requestSubmit() : form.submit();
                } finally {
                    isSubmitting = false;
                }
            }
        });
    }

    /**
     * Insert the reCaptcha token field to send with the form data.
     *
     * @param form
     */
    private insertRecaptchaTokenField(form: HTMLFormElement) {
        // Start refreshing the token for this form
        const formId = form.id;
        const action = form.getAttribute("data-recaptcha-action");

        if (!formId || !action) {
            console.warn("New form added without required attributes:", form);
            return;
        }

        // Create a hidden token field if not present
        if (!form.querySelector(`input[name="_recaptcha_token_${formId}"]`)) {
            const tokenFieldId = `recaptcha_token_${formId}`;
            form.insertAdjacentHTML(
                "beforeend",
                `<input type="hidden" name="_recaptcha_token_${formId}" id="${tokenFieldId}">`
            );
        }
    }

    /**
     * Automatically refresh the form token every 90 seconds.
     */
    private refreshTokens() {

        setInterval(() => {

            if (!this.recaptchaLoaded) return;

            document.querySelectorAll("[data-recaptcha-form]").forEach((form) => {
                const formId = form.id;
                const action = form.getAttribute("data-recaptcha-action");

                if (!action) return;

                const tokenField = form.querySelector(`input[name="_recaptcha_token_${formId}"]`) as HTMLInputElement;

                if (!tokenField) return;

                window.grecaptcha.execute(this.recaptchaPublicKey!, {action: action}).then((token: string) => {
                    tokenField.value = token;
                }).catch((error) => {
                    console.error("Error refreshing ReCaptcha token:", error);
                });
            });
        }, 90000);
    }

    private async setTokenForForm(form: HTMLFormElement): Promise<void> {
        const formId = form.id;
        const action = form.getAttribute("data-recaptcha-action");
        if (!formId || !action || !this.recaptchaLoaded) return;

        const tokenField = form.querySelector(`input[name="_recaptcha_token_${formId}"]`) as HTMLInputElement | null;
        if (!tokenField) return;

        const token = await window.grecaptcha.execute(this.recaptchaPublicKey!, {action});
        tokenField.value = token;

        // store “freshness” (seconds)
        tokenField.dataset.issuedAt = String(Math.floor(Date.now() / 1000));
    }

    /**
     * Sets up an Intersection Observer to monitor when forms are visible on the page.
     * It loads Recaptcha when a form becomes visible and then stops observing.
     */
    private observeVisibility() {
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                const form = entry.target as HTMLFormElement;
                this.insertRecaptchaTokenField(form);

                // mint token once when it first becomes visible
                this.setTokenForForm(form).catch(console.error);

                io.unobserve(form); // only once
            });
        }, {threshold: 0.1});

        document.querySelectorAll("[data-recaptcha-form]").forEach((form) => {
            io.observe(form as HTMLFormElement);
        });
    }
}

